import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import BoxCard from "../components/BoxCard";
import BoxModal from "../components/BoxModal";
import { partyBoxes, boxCategories } from "../data/partyBoxes";

const PartyBoxes = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedBox, setSelectedBox] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredBoxes = useMemo(() => {
    if (selectedCategory === "all") {
      return partyBoxes;
    }
    return partyBoxes.filter((box) => box.category === selectedCategory);
  }, [selectedCategory]);

  const handleViewDetails = (box) => {
    setSelectedBox(box);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBox(null);
  };

  return (
    <>
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>Shop Party Boxes</h1>
          <p>
            Curated party vibes, delivered to your door. Each box includes
            everything you need to transform your space — no planner required.
          </p>
        </div>
      </section>

      {/* Boxes Section */}
      <section className="bg-white pt-xl pb-2xl">
        <div className="container">
          {/* Category Filter */}
          <div className="boxes-filter">
            {boxCategories.map((category) => (
              <button
                key={category.id}
                className={`filter-btn ${
                  selectedCategory === category.id ? "active" : ""
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Boxes Grid */}
          <div className="boxes-grid">
            {filteredBoxes.map((box) => (
              <BoxCard
                key={box.id}
                box={box}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>

          {filteredBoxes.length === 0 && (
            <div className="text-center" style={{ padding: "4rem 0" }}>
              <p>
                No boxes found in this category. Try selecting a different
                filter.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Box Comparison Section */}
      <section className="bg-cream pt-2xl pb-2xl">
        <div className="container">
          <div className="section-header">
            <h2>Box Comparison</h2>
            <div className="section-divider"></div>
            <p>Find the perfect box for your celebration</p>
          </div>

          <div className="pricing-table" style={{ overflowX: "auto" }}>
            <table style={{ minWidth: "700px" }}>
              <thead>
                <tr>
                  <th>Box Name</th>
                  <th>Occasion</th>
                  <th>Vibe</th>
                  <th>Guests</th>
                  <th>Starting Price</th>
                </tr>
              </thead>
              <tbody>
                {partyBoxes.slice(0, 10).map((box) => (
                  <tr
                    key={box.id}
                    onClick={() => handleViewDetails(box)}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={{ fontWeight: "600" }}>{box.name}</td>
                    <td>
                      {boxCategories.find((c) => c.id === box.category)?.name ||
                        box.category}
                    </td>
                    <td style={{ maxWidth: "200px" }}>{box.vibe}</td>
                    <td>{box.guestRange}</td>
                    <td style={{ fontWeight: "600", color: "#c4a574" }}>
                      {box.tiers?.[0]?.price || "Contact"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* What's Inside Section */}
      <section className="bg-white pt-2xl pb-2xl">
        <div className="container">
          <div className="section-header">
            <h2>What's Inside Every Box?</h2>
            <div className="section-divider"></div>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "2rem",
              maxWidth: "1000px",
              margin: "0 auto",
            }}
          >
            {[
              {
                icon: "🎨",
                title: "Aesthetic-Forward Décor",
                desc: "Curated table elements, runners, centerpieces",
              },
              {
                icon: "✏️",
                title: "Signage & Accents",
                desc: "Themed banners, place cards, and printed pieces",
              },
              {
                icon: "🍽️",
                title: "Premium Tableware",
                desc: "Disposable but luxe plates, cups, and utensils",
              },
              {
                icon: "📋",
                title: "Setup Instructions",
                desc: "Step-by-step guide to style your space",
              },
              {
                icon: "📸",
                title: "Photo-Ready Details",
                desc: "Props and prompts for Instagram moments",
              },
            ].map((item, idx) => (
              <div key={idx} style={{ textAlign: "center", width: "250px" }}>
                <div
                  style={{
                    fontSize: "3rem",
                    marginBottom: "1rem",
                    width: "80px",
                    height: "80px",
                    background: "#f5ebe8",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 1rem",
                  }}
                >
                  {item.icon}
                </div>
                <h4>{item.title}</h4>
                <p style={{ fontSize: "0.9375rem", color: "#666" }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tiers Explanation */}
      <section className="bg-blush pt-2xl pb-2xl">
        <div className="container">
          <div className="section-header">
            <h2>Choose Your Experience</h2>
            <div className="section-divider"></div>
            <p>
              Every box comes in multiple tiers to fit your budget and needs
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "2rem",
              maxWidth: "1000px",
              margin: "0 auto",
            }}
          >
            <div
              style={{
                padding: "2rem",
                background: "#fff",
                borderRadius: "16px",
                textAlign: "center",
              }}
            >
              <h3 style={{ marginBottom: "0.5rem" }}>DIY Digital</h3>
              <p
                style={{
                  color: "#c4a574",
                  fontWeight: "600",
                  fontSize: "1.25rem",
                  marginBottom: "1rem",
                }}
              >
                $60
              </p>
              <p style={{ fontSize: "0.9375rem", color: "#666" }}>
                Guide PDF, printables, sourcing links, moodboard, and setup
                instructions. Perfect for the crafty host.
              </p>
            </div>
            <div
              style={{
                padding: "2rem",
                background: "#fff",
                borderRadius: "16px",
                textAlign: "center",
                border: "2px solid #c4a574",
                transform: "scale(1.05)",
              }}
            >
              <span
                style={{
                  background: "#c4a574",
                  color: "#fff",
                  padding: "0.25rem 1rem",
                  borderRadius: "20px",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                Most Popular
              </span>
              <h3 style={{ marginTop: "1rem", marginBottom: "0.5rem" }}>
                Standard Box
              </h3>
              <p
                style={{
                  color: "#c4a574",
                  fontWeight: "600",
                  fontSize: "1.25rem",
                  marginBottom: "1rem",
                }}
              >
                $150–250
              </p>
              <p style={{ fontSize: "0.9375rem", color: "#666" }}>
                Full decor kit shipped to your door. Includes tableware,
                signage, and themed accessories.
              </p>
            </div>
            <div
              style={{
                padding: "2rem",
                background: "#fff",
                borderRadius: "16px",
                textAlign: "center",
              }}
            >
              <h3 style={{ marginBottom: "0.5rem" }}>Luxe Box</h3>
              <p
                style={{
                  color: "#c4a574",
                  fontWeight: "600",
                  fontSize: "1.25rem",
                  marginBottom: "1rem",
                }}
              >
                $250–500
              </p>
              <p style={{ fontSize: "0.9375rem", color: "#666" }}>
                Upgraded linens, premium candles, printed menus, and extra
                styling elements for the ultimate experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white pt-2xl pb-2xl">
        <div className="container container-narrow">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <div className="section-divider"></div>
          </div>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
          >
            {[
              {
                q: "How long does setup take?",
                a: "30–75 minutes depending on the box size and your space. Each box includes a detailed setup guide to make it easy.",
              },
              {
                q: "Are the items reusable?",
                a: "Most décor items are reusable (candles, runners, centerpieces). Plates and glasses vary by tier — standard uses premium disposables, luxe includes reusable options.",
              },
              {
                q: "Do you offer local setup?",
                a: "Yes! In select areas, you can add on-site styling as an add-on at checkout. We'll set everything up so you can focus on being the host.",
              },
              {
                q: "Can I customize a box?",
                a: "Absolutely! Use our Build Your Box option or contact us to discuss custom color palettes, themes, or guest counts.",
              },
              {
                q: "How long does shipping take?",
                a: "Boxes ship 7–10 days after order. Rush options are available for an additional fee. We ship nationwide!",
              },
              {
                q: "What if I need to cancel or reschedule?",
                a: "We understand plans change! Contact us at least 14 days before your event date for a full refund or reschedule.",
              },
            ].map((faq, idx) => (
              <div
                key={idx}
                style={{
                  padding: "1.5rem",
                  background: "#faf8f5",
                  borderRadius: "12px",
                  borderLeft: "4px solid #c4a574",
                }}
              >
                <h4 style={{ marginBottom: "0.5rem" }}>{faq.q}</h4>
                <p style={{ margin: 0, color: "#666" }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Host Like a Pro?</h2>
          <p>
            A Pinterest-worthy party without having to fight your printer or cry
            into a roll of tulle. You've got this.
          </p>
          <div className="cta-buttons">
            <button
              className="btn btn-primary btn-lg"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              Shop Boxes
            </button>
            <Link to="/contact" className="btn btn-secondary btn-lg">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Box Detail Modal */}
      <BoxModal
        box={selectedBox}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default PartyBoxes;
